/**
 * Validates whether a given string is a valid email address based on RFC-like rules
 * and certain practical restrictions for local-part and domain formats.
 *
 * @param {string} email - The email address to validate.
 * @returns {boolean} `true` if the email is valid, otherwise `false`.
 */
export function isEmailValid(email) {
    // https://en.wikipedia.org/wiki/Email_address#Syntax
    // https://en.wikipedia.org/wiki/Email_address#Examples
    // https://en.wikipedia.org/wiki/Email_address#Internationalization
    const lastIndexOfAt = email.lastIndexOf('@');
    if (lastIndexOfAt === -1) {
        return false;
    }
    // The format of an email address is local-part@domain, where the local part may be up to 64 octets long and the domain may have a maximum of 255 octets.
    const localPart = email.substring(0, lastIndexOfAt);
    const domain = email.substring(lastIndexOfAt + 1);
    if (localPart.length > 64) {
        return false;
    }
    if (domain.length > 255) {
        return false;
    }
    // The local-part of the email address may be unquoted or may be enclosed in quotation marks.
    if (!(localPart.startsWith('"') && localPart.endsWith('"'))) {
        // If unquoted, it may use any of these ASCII characters:
        // - uppercase and lowercase Latin letters A to Z and a to z
        // - digits 0 to 9
        // - printable characters !#$%&'*+-/=?^_`{|}~
        // - dot ., provided that it is not the first or last character and provided also that it does not appear consecutively (e.g., John..Doe@example.com is not allowed).
        if (!/^[\p{L}\p{N}\p{M}!#$%&'*+-./=?^_`{|}~]+$/u.test(localPart)) {
            return false;
        }
        if (localPart.includes('..')) {
            return false;
        }
    }
    else {
        // If quoted, it may contain Space, Horizontal Tab (HT), any ASCII graphic except Backslash and Quote and a quoted-pair consisting
        // of a Backslash followed by HT, Space or any ASCII graphic; it may also be split between lines anywhere that HT or Space appears.
        // In contrast to unquoted local-parts, the addresses ".John.Doe"@example.com, "John.Doe."@example.com and "John..Doe"@example.com
        // are allowed.
        const unquotedLocalPart = localPart.substring(1, localPart.length - 1);
        // Remove escaped parts \" and \\, then local-part should not have " and \ characters anymore
        const unquotedLocalPartRemovedEscaped = unquotedLocalPart.replace(/\\["\\]/g, '');
        if (unquotedLocalPartRemovedEscaped.includes('"') || unquotedLocalPartRemovedEscaped.includes('\\')) {
            return false;
        }
    }
    // The domain name part of an email address has to conform to strict guidelines: it must match the requirements for a hostname, a list
    // of dot-separated DNS labels, each label being limited to a length of 63 characters and consisting of:
    // - uppercase and lowercase Latin letters A to Z and a to z;
    // - digits 0 to 9, provided that top-level domain names are not all-numeric;
    // - hyphen -, provided that it is not the first or last character.
    if (!(domain.startsWith('[') && domain.endsWith(']'))) {
        const domainParts = domain.split('.');
        // Local domain name with no TLD should technically be valid,
        // although ICANN highly discourages dotless email addresses, so we don't allow that.
        if (domainParts.length < 2) {
            return false;
        }
        for (const domainPart of domainParts) {
            if (!/^[\p{L}\p{N}\p{M}-]{1,64}$/u.test(domainPart)) {
                return false;
            }
            if (domainPart.startsWith('-') || domainPart.endsWith('-')) {
                return false;
            }
        }
        // ...provided that top-level domain names are not all-numeric
        if (/^\p{N}+$/u.test(domainParts[domainParts.length - 1])) {
            return false;
        }
    }
    else {
        // In addition, the domain may be an IP address literal, surrounded by square brackets [],
        // such as jsmith@[192.168.2.1] or jsmith@[IPv6:2001:db8::1]
        const domainIp = domain.substring(1, domain.length - 1).toLowerCase();
        if (domainIp.startsWith('ipv6:')) {
            const domainIpV6 = domainIp.substring(5);
            if (!/^[\da-f:]+$/.test(domainIpV6)) {
                return false;
            }
        }
        else {
            const domainIpV4Octets = domainIp.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
            if (!domainIpV4Octets) {
                return false;
            }
            for (const octet of domainIpV4Octets.splice(1)) {
                const octetNumber = parseInt(octet);
                if (octetNumber > 255 || (octetNumber < 100 && octet.startsWith('0'))) {
                    return false;
                }
            }
        }
    }
    return true;
}
